import * as amplitude from '@amplitude/analytics-react-native';
import {Platform} from 'react-native';

let initialized = false;

/**
 * Initialize Amplitude analytics with Session Replay.
 * Safe to call multiple times — subsequent calls are no-ops.
 *
 * Session Replay uses a native module (requireNativeComponent) that is not
 * available on web — it is dynamically imported only on native platforms.
 */
export async function initAnalytics(apiKey: string): Promise<void> {
  if (!apiKey) {
    console.warn('[Analytics] initAnalytics called with falsy API key — skipping initialization');
    return;
  }

  if (initialized) {
    return;
  }

  // minIdLength=1 because user ids in our system are small sequential integers
  // (e.g. "28"); the SDK default of 5 rejects them as invalid.
  await amplitude.init(apiKey, undefined, {minIdLength: 1}).promise;

  if (Platform.OS !== 'web') {
    const {SessionReplayPlugin} = await import('@amplitude/plugin-session-replay-react-native');
    await amplitude.add(new SessionReplayPlugin()).promise;
  }

  initialized = true;
}

/**
 * Track an analytics event.
 *
 * WARNING: Do NOT pass PII (passwords, card numbers, phone numbers, email
 * addresses, or any other personally identifiable information) in event names
 * or properties.
 *
 * @param eventName - Name of the event to track
 * @param properties - Optional key/value properties for the event (no PII)
 */
export function track(
  eventName: string,
  properties?: Record<string, unknown>,
  insertId?: string,
): void {
  if (!initialized) {
    return;
  }

  // SMG-4111: insertId is Amplitude's idempotency handle, and it protects
  // against RETRIES OF THIS CALL — not against the engine emitting the same
  // logical event. Amplitude scopes insert_id to device_id, and only client
  // events carry one, so the client's and the engine's keys never meet. Without
  // it the SDK generates a fresh UUID per call and a retried emission double
  // counts within this emitter.
  amplitude.track(eventName, properties, insertId ? {insert_id: insertId} : undefined);
}

/**
 * Identify a user in Amplitude.
 *
 * WARNING: Do NOT pass PII (passwords, card numbers, phone numbers, full
 * names, email addresses, or any other personally identifiable information)
 * in user properties.
 *
 * @param userId - Opaque user identifier (not an email or phone number)
 * @param properties - Optional user properties (no PII)
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, string | number | boolean>,
): void {
  if (!initialized) {
    return;
  }

  amplitude.setUserId(userId);

  if (properties && Object.keys(properties).length > 0) {
    const identifyEvent = new amplitude.Identify();
    Object.entries(properties).forEach(([key, value]) => {
      identifyEvent.set(key, value);
    });
    amplitude.identify(identifyEvent);
  }
}

/**
 * Reset the current user identity (e.g. on logout).
 */
export function resetUser(): void {
  if (!initialized) {
    return;
  }

  amplitude.reset();
}

/**
 * SMG-4111: the client's half of the Bet Placed metric.
 *
 * THE TWO EMITTERS DO NOT COVER EVERY ACCEPT. They are not duplicates of each
 * other, and they do NOT rely on Amplitude collapsing them:
 *
 *   - This one covers accepts THIS DEVICE REQUESTED. It hangs off the .then()
 *     of `acceptBetV2`, so it can only observe accepts this device asked for —
 *     which is narrower than "accepts bay-session performs".
 *   - The engine covers the accepts it performs itself (autoplay, and its own
 *     manual RPC).
 *
 * KNOWN GAP (SMG-4138): bay-session also accepts bets with NO client callback —
 * `runAutoTeeUpAccept` (fired by SetWagerMode, including the funds-default
 * effect that needs no player gesture) and `remintWager` (SwitchWagerCurrency).
 * Those write `accepted` smg_bet rows that neither emitter reports, and they are
 * not rare: bay-session-written accepts went from 0/day in early August to 560
 * on 2026-08-15. Do not treat a Bet Placed vs smg_bet reconciliation as a
 * pass/fail gate until SMG-4138 emits from bay-session's own insert.
 *
 * An earlier revision of this ticket claimed insert_id would let Amplitude
 * merge the two. That is wrong and worth recording so nobody rebuilds on it:
 * Amplitude's dedupe key is insert_id scoped to device_id, and client events
 * always carry a device_id (the React Native context plugin sets one) while
 * engine events carry none. The keys therefore never meet. insert_id is still
 * load-bearing HERE, for retries of this same call.
 *
 * Property parity with the engine is partial by necessity: venue_id, bet_type,
 * free_bet and debit_deferred are server-side columns that AcceptBetResponse
 * does not carry, so events from this emitter do not have them. Charts that
 * filter on those properties therefore EXCLUDE the mobile accept population.
 * Split on accept_source, or on the properties below, which both emitters share.
 */
export function trackBetPlaced(
  result: {betId?: unknown; betAmount?: unknown; currency?: unknown},
  acceptSource: 'manual' | 'autoplay' = 'manual',
): void {
  if (!result?.betId) {
    return;
  }

  // Prefer the SERVER's booked values over the request's. bay-session overrides
  // both: PRACTICE wagers are forced to amount 0, and an empty request currency
  // resolves to the sticky next_wager_currency. Reporting what we asked for
  // rather than what was booked would misstate handle for exactly those cases.
  const amountCents = typeof result.betAmount === 'bigint'
    ? Number(result.betAmount)
    : Number(result.betAmount ?? 0);
  const currency = (result.currency as string) || 'USD';

  const properties: Record<string, unknown> = {
    bet_id: String(result.betId),
    amount_cents: amountCents,
    betAmount: amountCents, // legacy key, pre-SMG-2440 charts
    currency,
    accept_source: acceptSource,
  };

  // amount_dollars only for real money, matching libs/analytics.IsRealMoney.
  // A points wager divided by 100 is dollar handle that does not exist.
  if (currency === 'USD') {
    properties.amount_dollars = amountCents / 100;
  }

  track('Bet Placed', properties, `bet-placed-${result.betId}`);
}
